import React, { useEffect, useCallback, useState, useMemo } from 'react';
import styled from 'styled-components';
import { SessionStepProps, SessionStaticProps } from './SessionFlowBase';
import { makeStyles, Typography } from '@material-ui/core';
import SessionTimeline from './Timeline/SessionTimeline';
import SessionPeriodList from './SessionPeriodList';
import VideoLayout, { PanMethod } from '../VideoPlayer/VideoLayout';
import { PeriodType } from '../../api';
import VideoControls from '../VideoPlayer/VideoControls';
import { TSession } from './Sessions';
import useSession, { refreshSession } from '../Util/useSession';
import sessionsService from '../../services/sessions';
import { Period as SessionPeriod, secondsToTimeSpan, timeSpanToSeconds } from '../../services/sessionModels';

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    textAlign: 'left',
  },
  text: {
    display: 'flex',
    marginRight: '10px',
  },
  flexContainer: {
    display: 'flex',
  },
  sidebar: {
    width: '20%',
    height: '50vh',
  },
  videoContainer: {
    width: '80%',
    height: '50vh',
    backgroundColor: '#f5f6fa',
  },
  videoControlsContainer: {
    marginTop: '12px',
    width: '20%',
    height: '12vh',
    backgroundColor: '#ccf1',
    border: '1px dashed #000',
  },
  timelineContainer: {
    marginTop: '12px',
    width: '80%',
    height: '12vh',
  },
  errorWindow: {
    position: 'fixed',
    backgroundColor: '#feec',
    border: '1px dashed #a00',
    color: '#a00',
    padding: '6px',
    zIndex: 5,
  },
  inlineText: {
    display: 'inline',
  },
  mediaControlIcon: {
    width: '28px',
    height: '28px',
  },
  mediaControlBigIcon: {
    width: '48px',
    height: '48px',
  },
}));

export const PeriodTypeToString = (type: string | undefined | null) => {
  switch (type) {
    case PeriodType.FirstHalf:
      return 'First Half';
    case PeriodType.SecondHalf:
      return 'Second Half';
    case PeriodType.ExtraTimeFirstHalf:
      return 'Extra Time - First Half';
    case PeriodType.ExtraTimeSecondHalf:
      return 'Extra Time - Second Half';
    case PeriodType.Penalties:
      return 'Penalties';
    default:
      return 'Other';
  }
};

export type Period = SessionPeriod;

interface Props extends SessionStepProps {}

const SessionPeriods: React.FunctionComponent<Props> & SessionStaticProps = ({ sessionid, setNextEnabled }) => {
  const { session } = useSession(sessionid);
  const classes = useStyles();

  //state
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [videoPlaying, setVideoPlaying] = useState<boolean>(false);
  const [playTime, setPlayTime] = useState<number>(0);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [seekVideo, setSeekVideo] = useState<boolean>(false);
  const [timeStep, setTimeStep] = useState<number>(5);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  // Videos
  const videos = session?.videos || [];

  //refs
  const divRef = React.useRef<HTMLDivElement>(null);

  const onSeek = useCallback((time: number) => {
    setSeekVideo(true);
  }, []);

  const absoluteSeek = useCallback(
    (newTime: number) => {
      setPlayTime((prev) => {
        // Clamp time to allowed values
        const clampedTime = Math.max(0, Math.min(newTime, videoDuration));
        onSeek(clampedTime);
        return clampedTime;
      });
    },
    [onSeek, videoDuration]
  );

  const relativeSeek = useCallback(
    (timeDiff: number) => {
      setPlayTime((prev) => {
        // Clamp time to allowed values
        const clampedTime = Math.max(0, Math.min(prev + timeDiff, videoDuration));
        onSeek(clampedTime);
        return clampedTime;
      });
    },
    [videoDuration, onSeek]
  );

  const onChangeVideoDuration = useCallback((duration: number) => {
    setVideoDuration(duration);
  }, []);

  const addPeriodCB = useCallback(() => {
    //Get our next period type in our enum that we don't have yet.
    let followUpType: PeriodType = PeriodType.Other;
    const periodTypes: string[] = Object.keys(PeriodType);
    let done: boolean = false;
    periodTypes.forEach((enumString: string) => {
      if (done) return;
      const enumValue: PeriodType = PeriodType[enumString as keyof typeof PeriodType];
      let foundTypePeriod: Period | undefined = session?.periods?.find((prev: Period) => prev.type === enumValue);
      if (!foundTypePeriod) {
        followUpType = enumValue;
        done = true;
      }
    });

    const time = secondsToTimeSpan(Math.round(playTime * 1000) / 1000);
    sessionsService
      .addPeriod(sessionid, { startTime: time, endTime: time, type: followUpType })
      .then(() => refreshSession(sessionid));
  }, [sessionid, session, playTime]);

  const deletePeriod = useCallback(
    (period: Period) => {
      sessionsService.deletePeriod(period.id).then(() => refreshSession(sessionid));
    },
    [sessionid]
  );

  const editPeriod = useCallback(
    (period: Period) => {
      sessionsService
        .updatePeriod(period.id, {
          startTime: period.startTime,
          endTime: period.endTime,
          type: period.type,
          label: period.label,
          teamLeftId: period.teamLeftId,
        })
        .then(() => refreshSession(sessionid));
    },
    [sessionid]
  );

  useEffect(() => {
    //check for errors here in the periods.
    let errorMsgs: string[] = [];
    let periods = session?.periods || [];

    //error message when there are no periods
    if (periods.length === 0) {
      errorMsgs.push('Atleast one period is required.');
    }

    //error message when more of the same period type (except other)
    const sortedPeriods = periods.slice().sort((a, b) => {
      let ret = a.type.localeCompare(b.type);
      if (ret === 0) {
        ret = (a.label || '').localeCompare(b.label || '');
      }
      return ret;
    });
    let results = [];
    for (let i = 0; i < sortedPeriods.length - 1; i++) {
      if (
        sortedPeriods[i + 1].type === sortedPeriods[i].type &&
        sortedPeriods[i + 1].label === sortedPeriods[i].label
      ) {
        results.push(sortedPeriods[i]);
      }
    }
    if (results.length > 0) {
      results.forEach((result) => {
        errorMsgs.push('Two ' + PeriodTypeToString(result.type) + ' periods with the same label');
      });
    }

    //error message when start or end is not set for a period
    periods.forEach((period: Period) => {
      if (!period.startTime) errorMsgs.push('Start time not set for [' + period.type + '].');
      if (!period.endTime) errorMsgs.push('End time not set for [' + period.type + '].');
    });

    //error message when end < start
    periods.forEach((period: Period) => {
      if (period.startTime && period.endTime && timeSpanToSeconds(period.endTime) < timeSpanToSeconds(period.startTime))
        errorMsgs.push('End comes earlier than start for [' + period.type + '].');
    });

    //if there are no other error messages, check if periods overlap
    if (errorMsgs.length === 0) {
      periods.forEach((period: Period) => {
        if (!period.startTime || !period.endTime) return;

        const start = timeSpanToSeconds(period.startTime);
        const end = timeSpanToSeconds(period.endTime);
        const segmentInBetween: Period | undefined = periods.find((check: Period) => {
          if (!check.startTime || !check.endTime) return false;
          const checkStart = timeSpanToSeconds(check.startTime);
          const checkEnd = timeSpanToSeconds(check.endTime);
          return (checkStart > start && checkStart < end) || (checkEnd > start && checkEnd < end);
        });
        if (segmentInBetween) {
          errorMsgs.push('Periods [' + period.type + '] and [' + segmentInBetween.type + '] overlap.');
        }
      });
    }

    setErrorMessages(errorMsgs);
  }, [session]);

  useEffect(() => {
    //set next button to enabled if we have no error messages
    const enabled: boolean = errorMessages.length === 0;

    setNextEnabled(enabled);
  }, [errorMessages, setNextEnabled]);

  const errorItems = useMemo(() => {
    return errorMessages.map((error: string) => {
      return <Typography key={error}>{error}</Typography>;
    });
  }, [errorMessages]);

  return (
    <CenterContainer>
      <Container>
        <Wrapper>
          <div className={classes.flexContainer}>
            <div className={classes.sidebar}>
              <SessionPeriodList
                periods={
                  session?.periods?.slice().sort((a: Period, b: Period) => timeSpanToSeconds(a.startTime) - timeSpanToSeconds(b.startTime)) || []
                }
                addPeriod={addPeriodCB}
                editPeriod={editPeriod}
                deletePeriod={deletePeriod}
                playTime={playTime}
                seekToPlayTime={absoluteSeek}
                hasExistingLabelPoints={
                  (session?.labelPositions && session.labelPositions.length > 0) || false
                }
              />
            </div>
            <div ref={divRef} className={classes.videoContainer}>
              {errorMessages.length > 0 && <div className={classes.errorWindow}>{errorItems}</div>}
              <VideoLayout
                videos={videos}
                showOverlayUI={false}
                updateIntervalMs={40}
                videoPlaying={videoPlaying}
                onChangeVideoPlaying={setVideoPlaying}
                playTime={playTime}
                playTimeChanged={setPlayTime}
                seekVideo={seekVideo}
                setSeekVideo={setSeekVideo}
                playbackSpeed={playbackSpeed}
                onChangeVideoDuration={onChangeVideoDuration}
                noFullscreen
                overridePanMethod={PanMethod.Drag}
              />
            </div>
          </div>
          <div className={classes.flexContainer}>
            <div className={classes.videoControlsContainer}>
              <VideoControls
                isPlaying={videoPlaying}
                onChangeVideoPlaying={setVideoPlaying}
                relativeSeek={relativeSeek}
                playbackSpeed={playbackSpeed}
                setPlaybackSpeed={setPlaybackSpeed}
                timeStep={timeStep}
                setTimeStep={setTimeStep}
              />
            </div>
            <div className={classes.timelineContainer}>
              <SessionTimeline
                playTime={playTime}
                seekToPlayTime={absoluteSeek}
                videoDuration={videoDuration}
                teamHome={session?.homeTeam}
                teamAway={session?.awayTeam}
                periods={session?.periods || []}
              />
            </div>
          </div>
        </Wrapper>
      </Container>
    </CenterContainer>
  );
};

const CenterContainer = styled.div`
  display: table-cell;
  height: calc(100vh - 350px);
  text-align: center;
  vertical-align: middle;
  width: 100%;
`;

const Container = styled.div`
  margin: auto;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px;
`;

SessionPeriods.isComplete = (session: TSession) => {
  return (session?.periods && session.periods.length > 0) || false;
};
export default SessionPeriods;
